import { useCallback, useEffect, useRef, useState } from 'react';
import { useIsAcomba } from './useIsAcomba';
import { useAcombaBridgeStore } from '../stores/acombaBridgeStore';
import { acombaBridgeService } from '../services/acombaBridgeService';
import { getBridgeUrl } from '../utils/bridge/bridgeConfig';
import { getTodayDate } from '../utils/sync/syncStartDate';
import { isDesktopEncaissementActive } from '../utils/transphere/desktopTransphereState';
import type { TransphereIndicatorDto } from '../types/transphere/transphereIndicator';

// Same window as the cloud indicator (TransphereIndicatorService.LookbackDays); change both together.
const LOOKBACK_DAYS = 90;

function getLookbackFromDate(): string {
    const d = new Date();
    d.setUTCDate(d.getUTCDate() - LOOKBACK_DAYS);
    return d.toISOString().slice(0, 10);
}

interface DesktopTransphereIndicator {
    /** What is collected but not recorded, or null when the banner must not be shown. */
    indicator: TransphereIndicatorDto | null;
    /** Whether the placeholder belongs on screen: the read is possible and has not answered yet. */
    showPlaceholder: boolean;
    /** Re-reads the aggregate. Safe to call on a non-desktop session — it does nothing there. */
    reload: () => Promise<void>;
}

/**
 * The Acomba desktop source for the Transphere reliability indicator.
 *
 * Desktop has no AcceoConnect, so the cloud route (GET /transphere/indicator) cannot answer: it resolves the
 * Transphere credential through GO.Connect, which a desktop session has none of. The bridge computes the
 * same aggregate locally instead — Transphere is called with the credential Acomba pushed to that machine,
 * and the "already recorded" half is read straight out of the company file.
 *
 * Returns `null` for anything that is not a real pending total: not a desktop session, the bridge not
 * connected, an unresolved read, or nothing pending. That collapse is
 * safe here and only here — the banner is advisory, so hiding it costs a warning, whereas showing a figure
 * the bridge never obtained would state that money is uncollected without having looked.
 */
export function useDesktopTransphereIndicator(): DesktopTransphereIndicator {
    const isAcomba = useIsAcomba();
    const sessionStatus = useAcombaBridgeStore(s => s.sessionStatus);
    // Read off Acomba itself — CITransphereActive and ControlCustomer.CCTransphereActive — rather than
    // from the credential push, which most Acomba builds never send. An unknown activation is not a
    // licence to ask, and a lapsed receipt subscription reads as not collecting here too.
    const transphereActive = useAcombaBridgeStore(s => s.companyTransphereActive);
    const encaissementActif = useAcombaBridgeStore(s => s.companyTransphereEncaissementActif);
    const receiptSubscription = useAcombaBridgeStore(s => s.companyTransphereReceiptSubscription);
    const collectionEnabled = transphereActive === true
        && isDesktopEncaissementActive(encaissementActif, receiptSubscription);
    const companyMatchStatus = useAcombaBridgeStore(s => s.companyMatchStatus);
    const canRead = isAcomba && sessionStatus === 'connected' && collectionEnabled
        && companyMatchStatus !== 'mismatch';

    const [indicator, setIndicator] = useState<TransphereIndicatorDto | null>(null);
    const [answered, setAnswered] = useState(false);

    // `reload` is handed to the page's shared refresh, which lists it among an effect's dependencies. Every
    // input it needs is therefore read through a ref rather than captured: a new identity per bridge state
    // change would re-run that effect, reloading the whole page's data two extra times per desktop visit.
    const canReadRef = useRef(canRead);
    canReadRef.current = canRead;

    // Distinguishes the answer we are waiting for from one already superseded. Home refreshes from several
    // triggers at once (visibility, panel restore, sync completion), and without this the slower of two
    // overlapping reads would win and pin a stale figure.
    const latestRead = useRef(0);

    const reload = useCallback(async () => {
        // Claimed before the gate is even consulted: a read already in flight belongs to a state that no
        // longer holds, so a closing gate has to supersede it too. Claiming this after the early return
        // below would let that read land afterwards and set a figure for a session that can no longer
        // show one.
        const read = ++latestRead.current;

        if (!canReadRef.current) {
            // Deliberately leaves `answered` alone. The gate opens a moment after mount, once the bridge
            // has connected and read the company — marking the read done here would consume the placeholder
            // before the one call that can fill it has even been made.
            setIndicator(null);
            return;
        }

        try {
            const toDate = getTodayDate();
            const result = await acombaBridgeService.getTransphereUnappliedPayments(
                getBridgeUrl(),
                getLookbackFromDate(),
                toDate,
            );
            if (read !== latestRead.current) return;

            // unresolvedReason set means Transphere could not be asked; count 0 then means nothing at all,
            // so both collapse to "show nothing" rather than to a reassuring zero. A net at or below zero
            // collapses the same way: a pending refund is a real row, but "-200.00 $ not applied" is not a
            // warning — the cloud path drops it for the same reason.
            setIndicator(result.resolved && result.count > 0 && result.totalAmount > 0
                // Same shape as the cloud answer, so one banner renders either source. The range would scope
                // a deep-link, which desktop never offers — it is carried because it is the honest window.
                ? { totalAmount: result.totalAmount, fromDate: result.oldestPaymentDate ?? toDate, toDate }
                : null);
        } catch {
            // Advisory: a bridge that is down, mid-restart or older than this endpoint just means no banner.
            if (read !== latestRead.current) return;
            setIndicator(null);
        } finally {
            if (read === latestRead.current) setAnswered(true);
        }
    }, []);

    // The gate opens after mount — the bridge connects, then reads the company — so the page's shared
    // refresh always runs at least once while it is still closed. Since `reload` is identity-stable, that
    // refresh will not re-run on its own account, and the opening has to trigger the first real read here.
    const wasReadable = useRef(canRead);
    useEffect(() => {
        if (canRead && !wasReadable.current) void reload();
        wasReadable.current = canRead;
    }, [canRead, reload]);

    // The placeholder answers to the read's own gate, not to a looser one: it stands for a read in flight,
    // so promising one the gate will refuse leaves a skeleton nothing can ever fill. `answered` is
    // deliberately left alone when the gate is shut (see `reload`), and a bridge session that expires
    // mid-extraction drops `sessionStatus` without clearing the company flags — so a placeholder gated on
    // the module alone would sit there for the rest of the visit.
    return { indicator, showPlaceholder: canRead && !answered, reload };
}
